import {
  Flex,
  IconButton,
  IconEditLine,
  IconTimerLine,
  IconTrashLine,
  IconUploadLine,
  Pill,
  Spinner,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router";
import { AssessmentPlanService } from "../../../services/AssessmentService";
import { Files } from "../../../services/FilesService";
import { TaskService } from "../../../services/TaskService";
import { waitForExpectedResult } from "../../../utils/Timer";
import {
  ERROR,
  INFO,
  SUCCESS,
  useNotification,
} from "../../CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  WORKING,
  useStatus,
} from "../../CustomHooks/StatusProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import PlanDataGrid from "./PlanDataGrid";

const APToggle = ({
  plan,
  allCycles,
  courseLevels,
  subjects,
  reloadPlans,
  assignmentTypes,
  programs,
  removePlanFromList,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { updateStatus, toggleStatus } = useStatus();
  const [pushPlanPending, setPushPlanPending] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(false);
  const [planEdit, setPlanEdit] = useState(false);
  const [fetchedPlan, setFetchedPlan] = useState(null);
  const navigate = useNavigate();

  async function fetchAssignmentDescriptions(plan) {
    try {
      const updatedSections = await Promise.all(
        plan.sections.map(async (section) => {
          const updatedAssessments = await Promise.all(
            section.assessments.map(async (assessment) => {
              if (assessment.assessment_type !== "flexible") {
                const description = await Files.getFileContent(
                  "response.html",
                  "assignmentDescription",
                  assessment.id
                );
                return {
                  ...assessment,
                  description: description.content || assessment.description,
                };
              }
              return assessment;
            })
          );
          return { ...section, assessments: updatedAssessments };
        })
      );
      return { ...plan, sections: updatedSections };
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("assessment.assignmentDescs"),
        }),
        ERROR
      );
      throw new Error("Failed to fetch assignment descriptions");
    }
  }

  function navigateToPlan(plan) {
    const state = {
      plan: plan,
      cycles: allCycles,
      courseLevels: courseLevels,
      assignmentTypes: assignmentTypes,
      acSubs: subjects,
      programs: programs,
    };
    navigate("manage-plan", { state: state });
  }

  useEffect(() => {
    // Redirect to manage-plan page if toggle is expanded, then the edit plan button is clicked
    if (planEdit && fetchedPlan) {
      navigateToPlan(fetchedPlan);
    }
  }, [planEdit, fetchedPlan]);

  const deletePlan = async () => {
    await AssessmentPlanService.deletePlan(plan.id)
      .then(() => {
        removePlanFromList(plan.id);
        setDeleteModal(false);
        showNotification(t("notifications.assessments.deletePlanSuccess"));
      })
      .catch(() => {
        showNotification(
          `${t("notifications.assessments.deletePlanError")}`,
          ERROR
        );
      });
  };

  const onAPToggle = async () => {
    // Fetch the assessment plan when plan hasn't been fetched yet, or when plan is already being fetched
    try {
      if (!fetchedPlan && !planExpanded && !planEdit) {
        setPlanExpanded(true);
        const fullPlan = await AssessmentPlanService.getAssessmentPlanById(
          plan.id
        );
        const planWithDescriptions =
          await fetchAssignmentDescriptions(fullPlan);
        setFetchedPlan(planWithDescriptions);
      }
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("assessment.assessmentPlan"),
        }),
        ERROR
      );
    } finally {
      setPlanExpanded(false);
    }
  };

  const onEditPlan = async () => {
    setPlanEdit(true);
    if (fetchedPlan) {
      navigateToPlan(fetchedPlan);
    } else if (!planExpanded) {
      try {
        const fullPlan = await AssessmentPlanService.getAssessmentPlanById(
          plan.id
        );
        const planWithDescriptions =
          await fetchAssignmentDescriptions(fullPlan);
        navigateToPlan(planWithDescriptions);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("assessment.assessmentPlan"),
          }),
          ERROR
        );
      } finally {
        setPlanEdit(false);
      }
    }
  };

  const isResultValid = (task) => {
    const progress = parseInt(
      (task.counter / task.obj.total_assignments) * 100
    );
    updateStatus({
      service: {
        name: t("status.pushPlanStatus"),
        status: progress === 100 ? GOOD : WORKING,
        progress: progress,
      },
    });
    return task.status === "done" || task.status === "error";
  };

  const processPushPlanToCanvasTask = (task) => {
    if (task.status === "done" && task.obj.total_assignments > 0) {
      reloadPlans();
      showNotification(
        t("notifications.analyze.pushPlanComplete", {
          total: task.counter,
          assignment:
            task.counter > 1
              ? t("assignment.assignments")
              : t("assignment.assignment"),
        }),
        SUCCESS
      );
    } else if (task.status === "done") {
      reloadPlans();
      showNotification(
        t("notifications.analyze.pushPlanAlreadyComplete"),
        SUCCESS
      );
    } else if (task.status === "error") {
      showNotification(
        t("notifications.analyze.pushPlanFailInProgress", {
          count: task.counter,
          total: task.obj.total_assignments,
        }),
        ERROR
      );
      updateStatus({
        service: { name: t("status.pushPlanStatus"), status: FAIL },
      });
    }
    setPushPlanPending(false);
  };

  const handleSendPlanError = () => {
    showNotification(t("notifications.analyze.pushPlanFail"), ERROR);
    updateStatus({
      service: { name: t("status.pushPlanStatus"), status: FAIL },
    });
    setPushPlanPending(false);
  };

  const sendPlanToCanvas = async (planId) => {
    setPushPlanPending(true);
    try {
      const task = await AssessmentPlanService.sendPlanToCanvas(planId);
      if (task.length === 0) {
        showNotification(
          t("notifications.analyze.pushPlanNoAssignments"),
          INFO
        );
        setPushPlanPending(false);
      } else {
        showNotification(
          <Text>
            {t("notifications.analyze.pushPlanProgress")}
            <Link
              themeOverride={{ fontWeight: "bold" }}
              onClick={() => toggleStatus(true)}
            >
              {t("status.checkStatus")}
            </Link>
          </Text>,
          SUCCESS,
          10000
        );
        updateStatus({
          service: {
            name: t("status.pushPlanStatus"),
            status: WORKING,
            progress: 0,
          },
        });
        waitForExpectedResult(
          () => TaskService.getTaskById(task.ddl_id),
          isResultValid,
          processPushPlanToCanvasTask,
          handleSendPlanError,
          30,
          3000
        );
      }
    } catch {
      showNotification(t("notifications.analyze.pushPlanFail"), ERROR);
      setPushPlanPending(false);
      updateStatus({
        service: { name: t("status.pushPlanStatus"), status: FAIL },
      });
    }
  };

  return (
    <>
      <ToggleGroup
        toggleLabel={t("assessment.toggleTitle", { outcome: plan.cycle.name })}
        onToggle={onAPToggle}
        summary={
          <Flex gap="x-small" wrap="wrap">
            <Flex.Item shouldGrow>
              {plan.cycle.name}:{" "}
              <Pill>
                {new Date(plan.cycle.start_date).toLocaleDateString()}
              </Pill>{" "}
              -{" "}
              <Pill>{new Date(plan.cycle.end_date).toLocaleDateString()}</Pill>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                renderTip={t("assessmentPlanningDashboard.trackCycleProgress")}
                on={["hover", "focus"]}
                placement="bottom"
              >
                <Link to={`/assessment-status/${plan.id}`}>
                  <IconButton
                    renderIcon={IconTimerLine}
                    screenReaderLabel={t(
                      "assessmentPlanningDashboard.trackCycleProgress"
                    )}
                  />
                </Link>
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                renderTip={t("assessment.editPlan")}
                on={["hover", "focus"]}
                placement="bottom"
              >
                <IconButton
                  renderIcon={
                    !planEdit ? (
                      IconEditLine
                    ) : (
                      <Spinner
                        renderTitle={t("global.pleaseWait")}
                        size="x-small"
                      />
                    )
                  }
                  onClick={onEditPlan}
                  disabled={planEdit}
                  screenReaderLabel={t("assessment.editPlan")}
                />
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                renderTip={t("assessment.deletePlanLabel")}
                on={["hover", "focus"]}
                placement="bottom"
              >
                <IconButton
                  renderIcon={IconTrashLine}
                  screenReaderLabel={t("assessment.deletePlanLabel")}
                  onClick={() => setDeleteModal(true)}
                />
              </Tooltip>
            </Flex.Item>
            <Flex.Item>
              <Tooltip
                renderTip={t("assessment.sendPlanLabel")}
                on={["hover", "focus"]}
                placement="bottom"
              >
                <IconButton
                  renderIcon={
                    pushPlanPending ? (
                      <Spinner
                        renderTitle={t("global.pleaseWait")}
                        size="x-small"
                      />
                    ) : (
                      <IconUploadLine />
                    )
                  }
                  screenReaderLabel={t("assessment.sendPlanLabel")}
                  onClick={() => sendPlanToCanvas(plan.id)}
                  disabled={pushPlanPending}
                />
              </Tooltip>
            </Flex.Item>
          </Flex>
        }
      >
        <View>
          {planExpanded || planEdit ? (
            <View as="div" margin="0 small">
              <Skeleton
                count={3}
                style={{
                  height: 64,
                  borderRadius: 5,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              />
            </View>
          ) : (
            <PlanDataGrid
              plan={fetchedPlan}
              assignmentTypes={assignmentTypes}
              subjects={subjects}
              courseLevels={courseLevels}
            />
          )}
        </View>
      </ToggleGroup>
      <DialogYesNo
        message={t("assessment.deleteAssessmentPlanConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={deletePlan}
      />
    </>
  );
};

export default APToggle;
